import { type MetaDescriptor } from 'react-router'
import { NO_INDEX_FOLLOW_ROBOTS } from './indexability'

type PublicEntityMetaInput = {
	title: string
	description?: string | null
	canonicalUrl: string
	imageUrl?: string | null
	shouldNoIndex?: boolean
	structuredData?: Record<string, unknown> | Array<Record<string, unknown>>
}

type PublicLearningStructuredDataInput = {
	title: string
	description?: string | null
	canonicalUrl: string
	imageUrl?: string | null
	language?: string | null
	level?: string | null
	durationMinutes?: number | null
	contentScope?: string | null
	primaryJurisdiction?: string | null
	providerName?: string
}

const NATIONAL_JURISDICTION_LABELS: Record<string, string> = {
	AR: 'Argentina',
	CL: 'Chile',
	CO: 'Colombia',
	ES: 'España',
	MX: 'México',
	PE: 'Perú',
	VE: 'Venezuela',
}

function toAbsoluteUrl(url: string | null | undefined, baseUrl: string) {
	if (!url) return null

	try {
		return new URL(url, baseUrl).toString()
	} catch {
		return null
	}
}

function buildNationalJurisdictionStructuredData(
	jurisdiction: string | null | undefined,
) {
	const code = jurisdiction?.trim().toUpperCase()
	if (!code || !/^[A-Z]{2}$/.test(code)) return null

	return {
		'@type': 'Country',
		name: NATIONAL_JURISDICTION_LABELS[code] ?? code,
		identifier: {
			'@type': 'PropertyValue',
			propertyID: 'ISO 3166-1 alpha-2',
			value: code,
		},
	}
}

export function buildPublicLearningStructuredData(
	input: PublicLearningStructuredDataInput,
) {
	const canonicalUrl = toAbsoluteUrl(input.canonicalUrl, input.canonicalUrl)
	const nationalJurisdiction =
		input.contentScope === 'national'
			? buildNationalJurisdictionStructuredData(input.primaryJurisdiction)
			: null
	const structuredData: Record<string, unknown> = {
		'@context': 'https://schema.org',
		'@type': 'Course',
		name: input.title,
		description: input.description || undefined,
		url: canonicalUrl || input.canonicalUrl,
		image: toAbsoluteUrl(input.imageUrl, input.canonicalUrl) || undefined,
		inLanguage: input.language || undefined,
		educationalLevel: input.level || undefined,
		timeRequired: input.durationMinutes
			? `PT${input.durationMinutes}M`
			: undefined,
		// Provider is only emitted when the active brand name is supplied; never
		// hardcode a brand here since this util serves all brands.
		provider: input.providerName
			? {
					'@type': 'Organization',
					name: input.providerName,
					url: canonicalUrl ? new URL('/', canonicalUrl).toString() : undefined,
				}
			: undefined,
		areaServed: nationalJurisdiction ?? undefined,
		spatialCoverage: nationalJurisdiction ?? undefined,
		audience: nationalJurisdiction
			? {
					'@type': 'Audience',
					geographicArea: nationalJurisdiction,
				}
			: undefined,
	}

	return Object.fromEntries(
		Object.entries(structuredData).filter(([, value]) => value !== undefined),
	)
}

type BreadcrumbItemInput = { name?: string | null; url?: string | null }

export function buildBreadcrumbStructuredData(
	items: BreadcrumbItemInput[],
	baseUrl: string,
): Record<string, unknown> | null {
	const valid = items
		.map((item) => ({
			name: item.name?.trim(),
			url: toAbsoluteUrl(item.url, baseUrl),
		}))
		.filter((item): item is { name: string; url: string } =>
			Boolean(item.name && item.url),
		)

	if (valid.length === 0) return null

	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: valid.map((item, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: item.name,
			item: item.url,
		})),
	}
}

type FaqItemInput = { question?: string | null; answer?: string | null }

export function buildFaqStructuredData(
	items: FaqItemInput[] | null | undefined,
): Record<string, unknown> | null {
	const valid = (items ?? [])
		.map((item) => ({
			question: item.question?.trim(),
			answer: item.answer?.trim(),
		}))
		.filter((item): item is { question: string; answer: string } =>
			Boolean(item.question && item.answer),
		)

	if (valid.length === 0) return null

	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: valid.map((item) => ({
			'@type': 'Question',
			name: item.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: item.answer,
			},
		})),
	}
}

type ItemListEntryInput = { name?: string | null; url?: string | null }

export function buildItemListStructuredData(
	items: ItemListEntryInput[] | null | undefined,
	baseUrl: string,
): Record<string, unknown> | null {
	const valid = (items ?? [])
		.map((item) => ({
			name: item.name?.trim(),
			url: toAbsoluteUrl(item.url, baseUrl),
		}))
		.filter((item): item is { name: string; url: string } =>
			Boolean(item.name && item.url),
		)

	if (valid.length === 0) return null

	return {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		itemListElement: valid.map((item, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: item.name,
			url: item.url,
		})),
	}
}

/** Drops null/empty entries and returns a JSON-LD graph (or a single node). */
export function composeStructuredDataGraph(
	nodes: Array<Record<string, unknown> | null | undefined>,
): Record<string, unknown> | Array<Record<string, unknown>> | undefined {
	const present = nodes.filter((node): node is Record<string, unknown> =>
		Boolean(node),
	)
	if (present.length === 0) return undefined
	if (present.length === 1) return present[0]
	return present
}

export function buildPublicEntityMetaDescriptors(
	input: PublicEntityMetaInput,
): MetaDescriptor[] {
	const description = input.description?.trim() ?? ''
	const canonicalUrl = toAbsoluteUrl(input.canonicalUrl, input.canonicalUrl)
	if (!canonicalUrl) {
		return [{ title: input.title }]
	}

	const imageUrl = toAbsoluteUrl(input.imageUrl, canonicalUrl)
	const twitterCard = imageUrl ? 'summary_large_image' : 'summary'

	return [
		{ title: input.title },
		{ name: 'description', content: description },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
		...(input.shouldNoIndex
			? [
					{ name: 'robots', content: NO_INDEX_FOLLOW_ROBOTS } as MetaDescriptor,
					{
						name: 'googlebot',
						content: NO_INDEX_FOLLOW_ROBOTS,
					} as MetaDescriptor,
				]
			: []),
		{ property: 'og:title', content: input.title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: canonicalUrl },
		{ property: 'og:type', content: 'website' },
		...(imageUrl
			? [
					{ property: 'og:image', content: imageUrl } as MetaDescriptor,
					{ property: 'og:image:alt', content: input.title } as MetaDescriptor,
				]
			: []),
		{ name: 'twitter:card', content: twitterCard },
		{ name: 'twitter:title', content: input.title },
		{ name: 'twitter:description', content: description },
		...(imageUrl
			? [
					{ name: 'twitter:image', content: imageUrl } as MetaDescriptor,
					{ name: 'twitter:image:alt', content: input.title } as MetaDescriptor,
				]
			: []),
		...(input.structuredData
			? [{ 'script:ld+json': input.structuredData } as MetaDescriptor]
			: []),
	]
}
